import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Frown } from "lucide-react";
import { DataTable } from "../components/common/DataTable";
import CreateImageSliderModal from "../components/imageSlider/CreateImageSliderModal";
import UpdateImageSliderModal from "../components/imageSlider/UpdateImageSliderModal";
import { fetchImageSliders, deleteImageSlider } from "../service/imageSliderService";
import { S3_BASE_URL } from "../utils/Constants";

export default function ImageSliders() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute(
      "dir",
      i18n.language === "ar" ? "rtl" : "ltr"
    );
  }, [i18n.language]);

  const { data: list = [], isLoading, isError, error } = useQuery({
    queryKey: ["imageSliders"],
    queryFn: fetchImageSliders,
  });

  const deleteMut = useMutation({
    mutationFn: deleteImageSlider,
    onSuccess: () => qc.invalidateQueries(["imageSliders"]),
  });

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
  if (isError) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 shadow-sm">
      <div className="flex items-center">
        <span className="text-red-700 font-medium">{error.message}</span>
      </div>
    </div>
  );

  const columns = [
    { key: "index", header: "#", isIndex: true, render: (_r, i) => i + 1 },
    { key: "location", header: t("location"), render: r => r.location },
    { 
      key: "imageUrls", 
      header: t("images"), 
      render: r => {
        const imageUrls = r.imageUrls || [];
        if (Array.isArray(imageUrls) && imageUrls.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {imageUrls.slice(0, 3).map((url, idx) => (
                <img
                  key={idx}
                  className="h-10 w-10 rounded object-cover border"
                  src={url?.startsWith?.('http') ? url : `${S3_BASE_URL}/${url}`}
                  alt={`Slider ${idx + 1}`}
                  onError={(e) => {
                    e.target.src = '/placeholder-image.png';
                  }}
                />
              ))}
              {imageUrls.length > 3 && (
                <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                  +{imageUrls.length - 3}
                </div>
              )}
            </div>
          );
        }
        return <span className="text-gray-400 text-sm">No images</span>;
      }
    },
    { key: "actions", header: t("actions"), render: r => (
      <div className="flex gap-2">
        <button 
          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          onClick={() => setEditingSlider(r)}
        >
          {t("edit")}
        </button>
        <button 
          className="px-3 py-1 text-xs font-medium text-red-600 bg-white border border-red-300 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => deleteMut.mutate(r.id)} 
          disabled={deleteMut.isLoading}
        >
          {t("delete")}
        </button>
      </div>
    )}
  ];

  return (
    <div className="p-6 flex flex-col">
      <div className="flex justify-between mb-4">
        <h1 className="text-2xl font-bold">{t("imageSliders")}</h1>
        <button 
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
          onClick={() => setShowCreateModal(true)}
        >
          {t("createImageSlider")}
        </button>
      </div>
      <DataTable data={list} columns={columns} rowKey={r => r.id} isLoading={isLoading} emptyState={(
        <div className="flex-1 flex flex-col items-center justify-center py-16">
          <Frown size={80} className="text-gray-300" />
          <p className="mt-4 text-xl text-gray-500">{t("noImageSliders")}</p>
        </div>
      )} />
      {showCreateModal && <CreateImageSliderModal onClose={() => setShowCreateModal(false)} />}
      {editingSlider && <UpdateImageSliderModal slider={editingSlider} onClose={() => setEditingSlider(null)} />}
    </div>
  );
}